'use client';

import {
  User,
  ArrowRight,
  Layers,
  GitBranch,
  Box,
  Workflow,
  MessageCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';

// Author's note / aside
interface AuthorNoteProps {
  children: ReactNode;
}

export function AuthorNote({ children }: AuthorNoteProps) {
  return (
    <aside className="my-10 not-prose">
      <div className="relative bg-gradient-to-r from-gray-50 to-white border-l-2 border-gray-300 pl-6 pr-5 py-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            <User className="w-4 h-4 text-gray-600" />
          </div>
          <p className="text-sm font-medium text-gray-500">Author&apos;s note</p>
        </div>
        <div className="text-gray-600 text-[15px] leading-relaxed italic [&>p]:m-0">
          {children}
        </div>
      </div>
    </aside>
  );
}

// Step-by-step process
interface Step {
  title: string;
  description?: string;
  tip?: string;
}

interface StepProcessProps {
  title?: string;
  steps: Step[];
}

export function StepProcess({ title, steps }: StepProcessProps) {
  return (
    <div className="my-10 not-prose">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{title}</h3>
      )}
      <div className="space-y-0">
        {steps.map((step, index) => (
          <div key={index} className="relative flex gap-6">
            {/* Line connector */}
            {index < steps.length - 1 && (
              <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200" />
            )}

            {/* Step number */}
            <div className="relative z-10 shrink-0">
              <div
                className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {index + 1}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 pb-8">
              <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
              {step.description && (
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              )}
              {step.tip && (
                <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <MessageCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{step.tip}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Framework diagram
interface FrameworkElement {
  title: string;
  description?: string;
  icon?: 'layers' | 'branch' | 'box' | 'flow';
}

interface FrameworkProps {
  title: string;
  elements: FrameworkElement[];
  layout?: 'horizontal' | 'vertical' | 'grid';
}

const frameworkIcons = {
  layers: Layers,
  branch: GitBranch,
  box: Box,
  flow: Workflow,
};

export function Framework({
  title,
  elements,
  layout = 'horizontal',
}: FrameworkProps) {
  const layoutClasses = {
    horizontal: 'flex flex-wrap justify-center gap-4',
    vertical: 'flex flex-col gap-4',
    grid: 'grid grid-cols-2 md:grid-cols-3 gap-4',
  };

  return (
    <div className="my-10 not-prose">
      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100">
        <h3
          className="text-xl font-normal text-gray-900 text-center mb-8"
          style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
        >
          {title}
        </h3>

        <div className={layoutClasses[layout]}>
          {elements.map((element, index) => {
            const Icon = element.icon
              ? frameworkIcons[element.icon]
              : frameworkIcons.box;

            return (
              <div
                key={index}
                className={`flex items-center gap-3 ${layout === 'horizontal' ? 'flex-1 min-w-[140px]' : ''}`}
              >
                {/* Arrow between elements (horizontal only) */}
                {layout === 'horizontal' && index > 0 && (
                  <ArrowRight className="w-4 h-4 text-gray-300 shrink-0 -ml-2 -mr-1" />
                )}

                <div className="flex-1 bg-white rounded-xl p-4 border border-gray-200 text-center">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="font-semibold text-gray-900 text-sm">
                    {element.title}
                  </p>
                  {element.description && (
                    <p className="text-xs text-gray-500 mt-1">
                      {element.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Highlighted text block
interface HighlightBlockProps {
  children: ReactNode;
  label?: string;
}

export function HighlightBlock({ children, label }: HighlightBlockProps) {
  return (
    <div className="my-10 not-prose">
      <div className="relative">
        {label && (
          <span className="absolute -top-3 left-6 px-2 py-0.5 bg-amber-400 text-xs font-semibold text-gray-900 rounded">
            {label}
          </span>
        )}
        <div
          className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 md:p-8 border border-amber-100 text-lg text-gray-800 leading-relaxed"
          style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// Numbered list with visual treatment
interface NumberedPoint {
  title: string;
  description?: string;
}

interface NumberedListProps {
  items: NumberedPoint[];
  startFrom?: number;
}

export function NumberedList({ items, startFrom = 1 }: NumberedListProps) {
  return (
    <div className="my-10 not-prose space-y-4">
      {items.map((item, index) => (
        <div key={index} className="flex gap-4">
          <div
            className="shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            {startFrom + index}
          </div>
          <div className="flex-1 pt-1">
            <p className="font-semibold text-gray-900">{item.title}</p>
            {item.description && (
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                {item.description}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Video embed placeholder
interface VideoEmbedProps {
  src: string;
  title?: string;
  aspectRatio?: '16:9' | '4:3' | '1:1';
}

export function VideoEmbed({
  src,
  title,
  aspectRatio = '16:9',
}: VideoEmbedProps) {
  const aspectClasses = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '1:1': 'aspect-square',
  };

  return (
    <figure className="my-10 not-prose">
      <div
        className={`${aspectClasses[aspectRatio]} bg-gray-100 rounded-xl overflow-hidden`}
      >
        <iframe
          src={src}
          title={title || 'Video'}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {title && (
        <figcaption className="mt-3 text-sm text-gray-500 text-center">
          {title}
        </figcaption>
      )}
    </figure>
  );
}

// Code with filename
interface CodeBlockProps {
  filename?: string;
  language?: string;
  children: ReactNode;
}

export function CodeBlock({ filename, language, children }: CodeBlockProps) {
  return (
    <div className="my-8 not-prose">
      {filename && (
        <div className="bg-gray-800 rounded-t-lg px-4 py-2 flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="text-sm text-gray-400 font-mono">{filename}</span>
          {language && (
            <span className="ml-auto text-xs text-gray-500 uppercase">
              {language}
            </span>
          )}
        </div>
      )}
      <div
        className={`bg-gray-900 text-gray-100 p-4 overflow-x-auto text-sm ${filename ? 'rounded-b-lg' : 'rounded-lg'}`}
      >
        {children}
      </div>
    </div>
  );
}

// Image with caption and optional zoom
interface FigureProps {
  src: string;
  alt: string;
  caption?: string;
  width?: number;
  height?: number;
}

export function Figure({ src, alt, caption, width, height }: FigureProps) {
  return (
    <figure className="my-10 not-prose">
      <div className="rounded-xl overflow-hidden bg-gray-100">
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className="w-full h-auto"
        />
      </div>
      {caption && (
        <figcaption className="mt-3 text-sm text-gray-500 text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// Tweet/Social embed card
interface SocialCardProps {
  author: string;
  handle: string;
  content: string;
  avatar?: string;
  platform?: 'twitter' | 'linkedin';
  href?: string;
}

export function SocialCard({
  author,
  handle,
  content,
  avatar,
  platform = 'twitter',
  href,
}: SocialCardProps) {
  const Wrapper = href ? 'a' : 'div';
  const wrapperProps = href
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <div className="my-8 not-prose">
      <Wrapper
        {...wrapperProps}
        className={`block bg-white border border-gray-200 rounded-xl p-5 ${href ? 'hover:border-gray-300 transition-colors' : ''}`}
      >
        <div className="flex items-center gap-3 mb-3">
          {avatar ? (
            <img
              src={avatar}
              alt={author}
              className="w-10 h-10 rounded-full"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-600 font-semibold">
                {author.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-900 text-sm">{author}</p>
            <p className="text-gray-500 text-sm">@{handle}</p>
          </div>
          <div className="ml-auto">
            {platform === 'twitter' && (
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            )}
            {platform === 'linkedin' && (
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            )}
          </div>
        </div>
        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
          {content}
        </p>
      </Wrapper>
    </div>
  );
}
